import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCampaign } from '../../contexts/CampaignContext/useCampaign';
import { CountdownTimer } from '../CountdownTimer';
import { getCurrentLanguage } from '../../utils/languageUtils';
import './style.less';

export const PromotionalBanner = () => {
  const [isDismissed, setIsDismissed] = useState(false);
  const { campaign, isCampaignActive } = useCampaign();
  const { i18n } = useTranslation();
  const currentLanguage = getCurrentLanguage(i18n);

  // Don't render if no active campaign or dismissed
  if (!isCampaignActive || !campaign || isDismissed) {
    return null;
  }

  // Handle close button click
  const handleClose = () => {
    setIsDismissed(true);
  };

  const { title, bannerStyle, button, endDate } = campaign;

  // Handle button action
  const handleButtonClick = (e) => {
    if (button.action.type === 'scroll' && button.action.target) {
      e.preventDefault();
      const element = document.querySelector(button.action.target);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    // For 'link' type, default Link behavior will handle it
  };

  // Render button based on action type
  const renderButton = () => {
    const buttonText = button.text[currentLanguage] || button.text.bg;

    if (button.action.type === 'link') {
      return (
        <Link
          to={button.action.url}
          className="banner-cta"
          aria-label={buttonText}
          tabIndex={0}
        >
          {buttonText}
          <i className="fas fa-arrow-right" aria-hidden="true"></i>
        </Link>
      );
    }

    if (button.action.type === 'scroll') {
      return (
        <button
          className="banner-cta"
          onClick={handleButtonClick}
          aria-label={buttonText}
          tabIndex={0}
        >
          {buttonText}
          <i className="fas fa-arrow-down" aria-hidden="true"></i>
        </button>
      );
    }

    return null;
  };

  // Animation variants
  const bannerVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  return (
    <AnimatePresence>
      <motion.div
        className="promotional-banner"
        style={{
          backgroundColor: bannerStyle.backgroundColor,
          color: bannerStyle.textColor,
        }}
        initial="hidden"
        animate="visible"
        exit={{ opacity: 0, y: -50 }}
        variants={bannerVariants}
        role="banner"
        aria-label="Promotional banner"
      >
        <div className="banner-container">
          <div className="banner-content">
            <div className="banner-icon" aria-hidden="true">
              <i className={`fas ${bannerStyle.icon}`}></i>
            </div>
            <CountdownTimer endDate={endDate} />
            <div className="banner-text">
              <h2 className="banner-title">{title[currentLanguage] || title.bg}</h2>
            </div>
          </div>
          <div className="banner-action">{renderButton()}</div>
          <button
            className="banner-close"
            onClick={handleClose}
            aria-label="Затвори банера"
            tabIndex={0}
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
